import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Loader2, Upload } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useState } from "react";

interface AiScrapingDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  type: "usps" | "testimonials";
  onSubmit: (data: { url?: string; file?: File }) => void;
  isLoading: boolean;
}

export default function AiScrapingDialog({
  open,
  onOpenChange,
  type,
  onSubmit,
  isLoading,
}: AiScrapingDialogProps) {
  const [url, setUrl] = useState("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [activeTab, setActiveTab] = useState("url");

  const handleSubmit = () => {
    if (activeTab === "url" && url.trim()) {
      onSubmit({ url: url.trim() });
    } else if (activeTab === "file" && selectedFile) {
      onSubmit({ file: selectedFile });
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const resetForm = () => {
    setUrl("");
    setSelectedFile(null);
    setActiveTab("url");
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      resetForm();
    }
    onOpenChange(open);
  };

  const canSubmit =
    (activeTab === "url" && url.trim()) ||
    (activeTab === "file" && selectedFile);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            AI-Powered {type === "usps" ? "USP" : "Testimonial"} Extraction
          </DialogTitle>
          <DialogDescription>
            Let AI analyze your content to extract{" "}
            {type === "usps"
              ? "key problems your product solves"
              : "customer testimonials"}
          </DialogDescription>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="url">Website URL</TabsTrigger>
            <TabsTrigger value="file">Upload File</TabsTrigger>
          </TabsList>

          <TabsContent value="url" className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="website-url">Website URL</Label>
              <Input
                id="website-url"
                type="url"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://your-website.com"
                disabled={isLoading}
              />
              <p className="text-xs text-muted-foreground">
                Enter the URL of your website, app store page, or landing page
              </p>
            </div>
          </TabsContent>

          <TabsContent value="file" className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="file-upload">Upload File</Label>
              <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-border p-6">
                {selectedFile ? (
                  <div className="text-center">
                    <div className="mb-2 h-8 w-8 mx-auto bg-blue-100 dark:bg-blue-900/20 rounded flex items-center justify-center">
                      <span className="text-xs text-blue-600 dark:text-blue-400">
                        {selectedFile.type.startsWith("image/")
                          ? "🖼️"
                          : selectedFile.type.startsWith("video/")
                            ? "🎥"
                            : selectedFile.type.includes("pdf")
                              ? "📄"
                              : selectedFile.type.includes("document")
                                ? "📝"
                                : "📄"}
                      </span>
                    </div>
                    <p className="text-sm font-medium">{selectedFile.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setSelectedFile(null)}
                      className="mt-2"
                    >
                      Remove
                    </Button>
                  </div>
                ) : (
                  <>
                    <Upload className="mb-2 h-8 w-8 text-primary/60" />
                    <p className="mb-1 text-sm font-medium">Upload a file</p>
                    <p className="mb-4 text-xs text-primary/60">
                      PDF, Word document, text file, image, or video
                    </p>
                    <input
                      id="file-upload"
                      type="file"
                      accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png,.gif,.webp,.mp4,.mov,.avi,.mkv"
                      onChange={handleFileChange}
                      className="hidden"
                      disabled={isLoading}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() =>
                        document.getElementById("file-upload")?.click()
                      }
                      disabled={isLoading}
                    >
                      Choose File
                    </Button>
                  </>
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                Upload marketing materials, product descriptions, customer
                feedback documents, screenshots, or promotional videos
              </p>
            </div>
          </TabsContent>
        </Tabs>

        <div className="flex justify-end gap-3 pt-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit || isLoading}
          >
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Analyzing...
              </>
            ) : (
              "Extract with AI"
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
